import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';

const sections = [
  { id: 'understand', label: 'DEFINITION', image: 'household' as const },
  { id: 'qualities', label: 'APTITUDE', image: 'table' as const },
  { id: 'learn', label: 'LEARNING', image: 'curriculum' as const },
  { id: 'courses', label: 'COURSES', image: 'assessment' as const },
  { id: 'assessment', label: 'ASSESSMENT', image: null },
  { id: 'certificate', label: 'CERTIFICATION', image: null },
  { id: 'career', label: 'CAREER', image: 'career' as const },
  { id: 'start', label: 'NEXT STEP', image: null },
];

const relatedLinks = [
  { path: '/butler-job', key: 'job' },
  { path: '/courses', key: 'courses' },
  { path: '/curriculum', key: 'curriculum' },
  { path: '/certificate', key: 'certificate' },
  { path: '/application', key: 'application' },
];

function splitParagraphs(value: string): string[] {
  const parts = value.split(/\n{2,}/).map((s) => s.trim()).filter((s) => s.length > 0);
  return parts.length > 0 ? parts : [value];
}

export default function GuideSections() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? `/en${path}` : path);

  return (
    <>
      {sections.map((section, idx) => (
        <section
          key={section.id}
          className={`relative py-20 md:py-28 ${
            idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'
          }`}
        >
          <div
            className={
              section.image
                ? 'max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16'
                : 'max-w-[860px] mx-auto px-8 md:px-12'
            }
          >
            <div className="text-center mb-12">
              <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
                {section.label}
              </p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                {t(`howToBecomeButler.${section.id}.title`)}
              </h2>
              <div className="gold-line mx-auto mb-6"></div>
              {splitParagraphs(t(`howToBecomeButler.${section.id}.intro`)).map((para, i) => (
                <p
                  key={i}
                  className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[680px] mx-auto"
                >
                  {para}
                </p>
              ))}
            </div>

            {section.image && (
              <div
                className={`flex flex-col ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } gap-6 md:gap-10 items-stretch bg-background-50 border border-background-200/60 mb-12`}
              >
                <div className="md:w-[42%] relative overflow-hidden min-h-[220px] md:min-h-[280px]">
                  <img
                    src={`/images/generated/how-to-become-butler-${section.image}-2026.png`}
                    alt={t(`howToBecomeButler.images.${section.image}`)}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                </div>
                <div className="md:w-[58%] p-6 md:p-10 flex flex-col justify-center">
                  {splitParagraphs(t(`howToBecomeButler.${section.id}.intro`)).map((para, i) => (
                    <p
                      key={i}
                      className="text-[16px] text-foreground-600 tracking-[0.04em] leading-[1.9]"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className={section.image ? 'max-w-[860px] mx-auto' : ''}>
              <div className="space-y-9">
                {[1, 2, 3].map((n) => (
                  <div key={n}>
                    <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.06em] mb-3 pl-4 border-l-2 border-accent-500/50">
                      {t(`howToBecomeButler.${section.id}.h${n}`)}
                    </h3>
                    {splitParagraphs(t(`howToBecomeButler.${section.id}.p${n}`)).map((para, i) => (
                      <p
                        key={i}
                        className="text-[16px] text-foreground-600 tracking-[0.04em] leading-[1.9]"
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="relative py-16 md:py-20 bg-primary-900">
        <nav
          aria-label={lang === 'en' ? 'Related pages' : '関連ページ'}
          className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-3"
        >
          {relatedLinks.map((link) => (
            <Link
              key={link.path}
              to={href(link.path)}
              className="border border-accent-500/50 px-4 py-3 text-center text-accent-300 tracking-[0.04em] hover:bg-accent-500/10 transition-colors"
            >
              {t(`howToBecomeButler.links.${link.key}`)}
            </Link>
          ))}
        </nav>
      </section>
    </>
  );
}
